/** GeneralTab — Contenido histórico del dashboard de Estadísticas (sin cambios de lógica).
 *
 * Filtros: rango de fechas (presets 7d/30d/90d/Todo/Mes + custom desde-hasta).
 * Muestra: KPIs, distribuciones (donut canal y prioridad), top responsables,
 * serie diaria de abiertos vs cerrados, y tiempos promedio.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Loader2, Inbox, CheckCircle2, AlertTriangle, Timer, ShieldCheck, AlertCircle, RefreshCw } from 'lucide-react';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import { useAuth } from '../../../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../../../hooks/useAbortableLoad';

const PRESETS = [
  { key: 'mes', label: 'Mes actual', days: null },
  { key: '7d',  label: '7 días',     days: 7 },
  { key: '30d', label: '30 días',    days: 30 },
  { key: '90d', label: '90 días',    days: 90 },
  { key: 'all', label: 'Todo',       days: 'all' },
];

const PIE_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];

const fmtHoras = (h) => h == null ? '—' : (h < 1 ? `${Math.round(h * 60)} min` : `${h} h`);

function startOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function endOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}
function todayISO() { return new Date().toISOString().slice(0, 10); }
function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function GeneralTab() {
  const { api, empresa } = useAuth();
  const navigate = useNavigate();
  const [preset, setPreset] = useState('all');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [overview, setOverview] = useState(null);
  const [throughput, setThroughput] = useState(null);
  const [canales, setCanales] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [vencimientos, setVencimientos] = useState(null);
  const [loading, setLoading] = useState(true);

  const aplicarPreset = (key) => {
    setPreset(key);
    if (key === 'mes') { setDesde(startOfMonth()); setHasta(endOfMonth()); return; }
    if (key === 'all') { setDesde(''); setHasta(''); return; }
    const found = PRESETS.find((p) => p.key === key);
    if (found?.days) { setDesde(daysAgoISO(found.days)); setHasta(todayISO()); }
  };

  const fetchAll = async (signal) => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const [ov, tp, c, p] = await Promise.all([
        api.get('/tickets/stats/overview', { params, signal }),
        api.get('/tickets/stats/throughput', { params, signal }),
        api.get('/tickets/canales', { signal }),
        api.get('/tickets/prioridades', { signal }),
      ]);
      setOverview(ov.data);
      setThroughput(tp.data);
      setCanales(c.data || []);
      setPrioridades(p.data || []);
      // Responsables: solo para etiquetar gráficas. Aislado para que un 403
      // (rol con permiso de Estadísticas pero no de Tickets) no borre los datos.
      api.get('/tickets/responsables', { signal })
        .then((r) => setResponsables(r.data || []))
        .catch(() => setResponsables([]));
      api.get('/tickets/stats/vencimientos', { signal })
        .then((v) => setVencimientos(v.data))
        .catch(() => setVencimientos(null));
    } catch (e) {
      if (isCanceled(e)) return;
      console.error('Error cargando estadísticas:', e);
    } finally {
      setLoading(false);
    }
  };

  useAbortableLoad((signal) => {
    if (empresa?.modulo_tickets_activo) fetchAll(signal); else setLoading(false);
  }, [desde, hasta]);

  const nombreCanal = (id) => canales.find((c) => c.id === id)?.nombre || 'Sin canal';
  const nombrePrioridad = (id) => prioridades.find((p) => p.id === id)?.nombre || 'Sin prioridad';
  const colorPrioridad = (id) => prioridades.find((p) => p.id === id)?.color;
  const nombreResp = (id) => responsables.find((r) => r.id === id)?.nombre || (id ? id.slice(0, 8) : 'Sin asignar');

  const dataCanal = useMemo(() => {
    if (!overview?.distribucion?.por_canal_id) return [];
    return Object.entries(overview.distribucion.por_canal_id).map(([id, v]) => ({
      name: nombreCanal(id), value: v,
    }));
  }, [overview, canales]); // eslint-disable-line

  const dataPrioridad = useMemo(() => {
    if (!overview?.distribucion?.por_prioridad_id) return [];
    return Object.entries(overview.distribucion.por_prioridad_id).map(([id, v]) => ({
      name: nombrePrioridad(id), value: v, color: colorPrioridad(id),
    }));
  }, [overview, prioridades]); // eslint-disable-line

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <p className="text-sm text-slate-500">Métricas del Centro de Ayuda. Por defecto: todos los tickets.</p>
        <div className="flex flex-wrap items-end gap-2" data-testid="filtros-estadisticas">
          {PRESETS.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={preset === p.key ? 'default' : 'outline'}
              onClick={() => aplicarPreset(p.key)}
              data-testid={`preset-${p.key}`}
            >
              {p.label}
            </Button>
          ))}
          <div className="flex items-center gap-1 ml-2">
            <Input
              type="date"
              value={desde}
              onChange={(e) => { setPreset('custom'); setDesde(e.target.value); }}
              className="h-9 w-[150px]"
              data-testid="input-desde"
            />
            <span className="text-xs text-slate-400">→</span>
            <Input
              type="date"
              value={hasta}
              onChange={(e) => { setPreset('custom'); setHasta(e.target.value); }}
              className="h-9 w-[150px]"
              data-testid="input-hasta"
            />
          </div>
          <Button size="sm" variant="ghost" onClick={() => fetchAll()} data-testid="refrescar-general" className="text-slate-600">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
        </div>
      </div>

      {loading || !overview ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      ) : (
        <div className="space-y-4">
          {overview.totales.total === 0 && (desde || hasta) && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-3"
              data-testid="banner-no-datos-rango"
            >
              <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium text-amber-900">No hay tickets en este rango</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Prueba ampliando el rango de fechas o ver todos los tickets sin filtro.
                </div>
              </div>
              <button
                onClick={() => aplicarPreset('all')}
                className="text-xs font-medium text-amber-900 underline hover:no-underline whitespace-nowrap"
                data-testid="btn-banner-ver-todos"
                type="button"
              >
                Ver todos
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI icon={Inbox}        label="Total"     value={overview.totales.total}    color="text-slate-700" testid="kpi-total"
                 onClick={() => navigate('/admin/centro-ayuda/tickets')}
                 hint="Ver todos los tickets" />
            <KPI icon={AlertTriangle} label="Abiertos" value={overview.totales.abiertos} color="text-amber-600" testid="kpi-abiertos"
                 onClick={() => navigate('/admin/centro-ayuda/tickets?estado=ABIERTO')}
                 hint="Ver tickets abiertos" />
            <KPI icon={CheckCircle2} label="Cerrados"  value={overview.totales.cerrados}  color="text-emerald-600" testid="kpi-cerrados"
                 onClick={() => navigate('/admin/centro-ayuda/tickets?estado=TERMINADO')}
                 hint="Ver tickets cerrados" />
            <KPI icon={ShieldCheck}
                 label="SLA cumplido"
                 value={overview.totales.sla_ratio_pct != null ? `${overview.totales.sla_ratio_pct}%` : '—'}
                 sub={overview.totales.evaluados_sla > 0 ? `${overview.totales.sla_cumplidos}/${overview.totales.evaluados_sla}` : 'sin datos'}
                 color="text-emerald-600"
                 testid="kpi-sla"
                 onClick={
                   overview.totales.evaluados_sla > overview.totales.sla_cumplidos
                     ? () => navigate('/admin/centro-ayuda/tickets?estado=TERMINADO&sla=incumplido')
                     : null
                 }
                 hint={
                   overview.totales.evaluados_sla > overview.totales.sla_cumplidos
                     ? 'Ver tickets con SLA incumplido'
                     : null
                 } />
            <KPI icon={Timer}
                 label="Tiempo prom. cierre"
                 value={fmtHoras(overview.tiempos.promedio_cierre_horas)}
                 sub={`1ra resp: ${fmtHoras(overview.tiempos.promedio_primera_resp_horas)}`}
                 color="text-slate-700"
                 testid="kpi-tiempo" />
          </div>

          {vencimientos && vencimientos.total > 0 && (
            <Card data-testid="panel-vencimientos" className="mt-3">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Timer className="w-4 h-4 text-slate-500" /> Plazo legal (días hábiles)
                  <span className="text-xs font-normal text-slate-400">· {vencimientos.total} tickets activos</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <SemaforoCard label="Vencidos" value={vencimientos.vencidos} tone="red" testid="venc-vencidos"
                    onClick={() => navigate('/admin/centro-ayuda/tickets?venc=vencido')} />
                  <SemaforoCard label="Por vencer" value={vencimientos.por_vencer} tone="amber" testid="venc-por-vencer"
                    onClick={() => navigate('/admin/centro-ayuda/tickets?venc=por_vencer')} />
                  <SemaforoCard label="En plazo" value={vencimientos.en_plazo} tone="green" testid="venc-en-plazo" />
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Distribución por canal</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={dataCanal} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={2}>
                      {dataCanal.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Distribución por prioridad</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={dataPrioridad} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={2}>
                      {dataPrioridad.map((d, idx) => <Cell key={idx} fill={d.color || PIE_COLORS[idx % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Top 5 responsables (por carga)</CardTitle></CardHeader>
              <CardContent>
                {(!overview.top_responsables_por_carga?.length) ? (
                  <p className="text-sm text-slate-400 italic py-6 text-center">Sin datos</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={overview.top_responsables_por_carga.map((r) => ({ name: nombreResp(r.responsable_id), value: r.total }))}
                      layout="vertical"
                      margin={{ left: 16, right: 16, top: 8, bottom: 8 }}
                    >
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Tickets abiertos vs cerrados por día</CardTitle>
            </CardHeader>
            <CardContent>
              {!throughput?.serie?.length ? (
                <p className="text-sm text-slate-400 italic py-10 text-center">Sin datos en este rango.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={throughput.serie} margin={{ left: 8, right: 16, top: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="abiertos" name="Abiertos" stroke="#f59e0b" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="cerrados" name="Cerrados" stroke="#16a34a" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {overview.top_responsables_por_sla?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Top responsables por cumplimiento SLA</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tabla-top-sla">
                    <thead className="bg-slate-50 text-xs text-slate-600">
                      <tr><th className="text-left px-3 py-2">Responsable</th><th className="text-right px-3 py-2">Cerrados evaluados</th><th className="text-right px-3 py-2">SLA cumplidos</th><th className="text-right px-3 py-2">% cumplimiento</th></tr>
                    </thead>
                    <tbody>
                      {overview.top_responsables_por_sla.map((r) => (
                        <tr key={r.responsable_id} className="border-t hover:bg-slate-50">
                          <td className="px-3 py-2">{nombreResp(r.responsable_id)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.total}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.ok}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">{Math.round(r.ratio)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, color = 'text-slate-700', testid, onClick, hint }) {
  const clickable = typeof onClick === 'function';
  return (
    <Card
      className={clickable ? 'cursor-pointer hover:shadow-md hover:border-slate-300 transition-all' : ''}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
      title={hint || undefined}
      data-testid={clickable ? `${testid}-clickable` : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 mb-1">
          <Icon className={`w-3.5 h-3.5 ${color}`} /> {label}
        </div>
        <div className={`text-2xl font-bold ${color}`} data-testid={testid}>{value}</div>
        {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
        {hint && clickable && <div className="text-[10px] text-blue-600 mt-1.5 hover:underline">{hint} →</div>}
      </CardContent>
    </Card>
  );
}

function SemaforoCard({ label, value, tone, testid, onClick }) {
  const clickable = typeof onClick === 'function';
  const tones = {
    red:   { dot: 'bg-red-500',    text: 'text-red-700',    ring: 'hover:border-red-300' },
    amber: { dot: 'bg-amber-500',  text: 'text-amber-700',  ring: 'hover:border-amber-300' },
    green: { dot: 'bg-emerald-500',text: 'text-emerald-700',ring: 'hover:border-emerald-300' },
  }[tone] || { dot: 'bg-slate-400', text: 'text-slate-700', ring: '' };
  return (
    <div
      className={`rounded-lg border border-slate-200 p-4 transition-all ${clickable ? `cursor-pointer hover:shadow-md ${tones.ring}` : ''}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
      title={clickable ? `Ver tickets ${label.toLowerCase()}` : undefined}
      data-testid={clickable ? `${testid}-clickable` : testid}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 mb-1">
        <span className={`w-2 h-2 rounded-full ${tones.dot}`} /> {label}
      </div>
      <div className={`text-3xl font-bold ${tones.text}`} data-testid={testid}>{value}</div>
      {clickable && value > 0 && <div className="text-[10px] text-blue-600 mt-1.5">Ver en tablero →</div>}
    </div>
  );
}
